import React, { useEffect, useMemo, useState, useRef } from 'react';
import MaterialTable, { MTableBody } from 'material-table';
import { Button } from 'react-bootstrap';
import {
  TableCell, TableFooter, TableRow, Tooltip,
} from '@material-ui/core';
import Edit from '@material-ui/icons/Edit';
import Delete from '@material-ui/icons/Delete';
import { styledTable, styledTableHeading, tableIcons } from '../../../helpers';
import useAPI from '../../../hooks/useAPI';
import {
  getCustomFilterAndSearch, localization, modifyDefaultOptions, options, tableIconsModified,
} from '../../../helpers/table';
import { mapIdsToLabels } from '../../../helpers/plansHelper';
import { floatRenderer } from '../../../services/formatSvc';
import { useAPIStratProjectsPlan } from '../adminApiService';
import EditSelect from '../../../components/table/EditSelect';
import EditSelectYear from '../../../components/table/EditSelectYear';
import { MonetaryFloatInput } from '../../../components/common/MonetaryFloatInput';
import useDataReducer from './useDataReducer';
import MTableHeader from '../../../components/material-table-overrides/MTableHeader';
import MTableFilterRow from '../../../components/material-table-overrides/MTableFilterRow';
import { getLookupObject, getRenderedLookupObject } from '../../../utils/table';
import FilterMultiSelect from '../../../components/table/FilterMultiSelect';
import DownloadLink from '../../../components/common/DownloadLink';
import LoadingTable from '../../../components/table/LoadingTable';

export default function TabStratProjectsPlan(props) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, dispatchData] = useDataReducer();
  const MTableFiltersRef = useRef({});
  const api = useAPI();
  const [getData, saveData, removeData] = useMemo(() => useAPIStratProjectsPlan(api), [api]);

  useEffect(async () => {
    const result = await getData();
    setIsLoaded(true);
    if (props.plansDictionary) {
      dispatchData(() => result.map((el) => ({
        ...mapIdsToLabels(props.plansDictionary, el),
        ...el,
      })));
    }
  }, [props.plansDictionary]);

  const filterLookups = useMemo(() => {
    const result = {};

    if (props.plansDictionary) {
      result.strategies = getRenderedLookupObject(props.plansDictionary.strategies, 'name');
      result.agreements = getRenderedLookupObject(props.plansDictionary.agreements, 'num');
      result.bcCodes = getRenderedLookupObject(props.plansDictionary.bcCodes, 'fullCode');
      result.fundings = getRenderedLookupObject(props.plansDictionary.fundings, 'name');
    }

    if (data.entries.length) {
      result.years = getLookupObject(data.entries, 'year');
    }

    return result;
  }, [props.plansDictionary, data.entries]);

  const handleSaveData = async (entry) => {
    const resp = await saveData({
      ...entry,
      year: parseInt(entry.year),
    });
    if (!resp || resp.error) return null;
    return { ...resp, ...mapIdsToLabels(props.plansDictionary, resp) };
  };

  const handleRemoveData = async (entry) => {
    const resp = await removeData(entry);
    if (!resp || resp.error) return null;
    return resp;
  };

  const renderTooltip = (content, tooltip) => (
    <Tooltip placement="top" title={<span style={{ fontSize: 14 }}>{tooltip}</span>}>
      <span>{content}</span>
    </Tooltip>
  );
  const renderEditYearComponent = (_props) => (
    <EditSelectYear value={_props.value} onChange={_props.onChange} />);
  const renderEditStrategyComponent = (_props) => (
    <EditSelect value={_props.value} onChange={_props.onChange} data={props.plansDictionary.strategies} />);
  const renderEditAgreementComponent = (_props) => (
    <EditSelect value={_props.value} onChange={_props.onChange} data={props.plansDictionary.agreements} />);
  const renderEditBcCodeComponent = (_props) => (
    <EditSelect value={_props.value} onChange={_props.onChange} data={props.plansDictionary.bcCodes} />);
  const renderEditFundingComponent = (_props) => (
    <EditSelect value={_props.value} onChange={_props.onChange} data={props.plansDictionary.fundings} />);

  const getTotal = (renderData) => new Intl.NumberFormat('ru', {minimumFractionDigits: 2, maximumFractionDigits: 2}).format(renderData.reduce((acc, entry) => acc + entry.sum, 0));

  const columns = [
    {
      title: 'Год',
      field: 'year',
      lookup: filterLookups.years,
      filterComponent: ({ columnDef, onFilterChanged })=><FilterMultiSelect columnDef={columnDef} onFilterChanged={onFilterChanged} MTableFiltersRef={MTableFiltersRef} field="year" />,
      filterCellStyle: { width: '30px' },
      editComponent: renderEditYearComponent,
    },
    {
      title: 'Стратегический проект / Политики',
      field: 'strategyId',
      lookup: filterLookups.strategies,
      filterComponent: ({ columnDef, onFilterChanged })=><FilterMultiSelect columnDef={columnDef} onFilterChanged={onFilterChanged} MTableFiltersRef={MTableFiltersRef} field="strategyId" />,
      editComponent: renderEditStrategyComponent,
      render: ({ strategy }) => strategy,
    },
    {
      title: 'Номер соглашения',
      field: 'agreementId',
      lookup: filterLookups.agreements,
      filterComponent: ({ columnDef, onFilterChanged })=><FilterMultiSelect columnDef={columnDef} onFilterChanged={onFilterChanged} MTableFiltersRef={MTableFiltersRef} field="agreementId" />,
      editComponent: renderEditAgreementComponent,
      render: ({ agreementNum, agreement }) => renderTooltip(agreementNum, agreement),
    },
    {
      title: 'Код по бюджетной классификации',
      field: 'bcCodeId',
      lookup: filterLookups.bcCodes,
      filterComponent: ({ columnDef, onFilterChanged })=><FilterMultiSelect columnDef={columnDef} onFilterChanged={onFilterChanged} MTableFiltersRef={MTableFiltersRef} field="bcCodeId" />,
      filterCellStyle: { width: '14%' },
      editComponent: renderEditBcCodeComponent,
      render: ({ bcCode, bcCodeNum }) => renderTooltip(bcCodeNum, bcCode),
    },
    {
      title: 'Тип финансирования',
      field: 'fundingId',
      lookup: filterLookups.fundings,
      filterComponent: ({ columnDef, onFilterChanged })=><FilterMultiSelect columnDef={columnDef} onFilterChanged={onFilterChanged} MTableFiltersRef={MTableFiltersRef} field="fundingId" />,
      editComponent: renderEditFundingComponent,
      render: ({ funding }) => funding,
    },
    {
      title: 'План, руб.',
      field: 'sum',
      editComponent: (_props) => <MonetaryFloatInput value={_props.value} onChange={_props.onChange} table />,
      render: (rowData) => floatRenderer(rowData.sum),
      filterCellStyle: { width: '260px' },
    },
  ];

return (
    <section className="tab-strat-projects-plan">
      <div style={styledTable}>
        <MaterialTable
          title={(
            <div className="d-flex align-items-center">
              <h3 style={{ marginBottom: 0 }}>План по стратегическим проектам / политикам развития</h3>
              <DownloadLink
                variant="outline-primary"
                url="/mng/strategies/0/plans/export"
                filename="План по стратегическим проектам / политикам развития.xlsx"
                style={{ marginLeft: 16 }}
              >
                Excel
              </DownloadLink>
            </div>
)}
          options={modifyDefaultOptions({
            actionsColumnIndex: -1,
            addRowPosition: 'first',
            maxBodyHeight: 'calc(100vh - 442px)',
            headerStyle: { height: 48, zIndex: 0 },
            filterCellStyle: {
              position: 'sticky',
              top: 48,
              background: 'white',
              zIndex: 5,
            },
          })}
          icons={tableIconsModified}
          components={{
            Header: (props) => (<MTableHeader {...props} tableHeadStyle={styledHeaderSticky} />),
            FilterRow: (props) => (<MTableFilterRow {...props} />),
            Body: (_props) => (
              <LoadingTable spinnerStyle={{marginTop: '40px'}} loadingObject={{isLoaded}}>
                <MTableBody {..._props} />
                <TableFooter style={{ position: 'sticky', bottom: '0' }}>
                  <TableRow style={{ backgroundColor: '#FFF' }}>
                    <TableCell colSpan={4} />
                    <TableCell style={{ color: 'inherit' }}>Итого</TableCell>
                    <TableCell style={{ color: 'inherit' }} colSpan={2}>{getTotal(_props.renderData)}</TableCell>
                  </TableRow>
                </TableFooter>
              </LoadingTable>
            ),
          }}
          editable={{
            onRowAdd: (newData) =>
              new Promise(async (resolve, reject) => {
                const resp = await handleSaveData(newData);
                if (resp) await dispatchData((prev) => [resp, ...prev]);
                resolve();
              }),
            onRowUpdate: (newData, oldData) =>
              new Promise(async (resolve, reject) => {
                const resp = await handleSaveData(newData);
                if (resp) await dispatchData((prev) => prev.map((d) => (d.id === resp.id ? resp : d)));
                resolve();
              }),
            onRowDelete: (oldData) =>
              new Promise(async (resolve, reject) => {
                const resp = await handleRemoveData(oldData);
                if (resp) await dispatchData((prev) => prev.filter((v) => v.id !== oldData.id));
                resolve();
              }),
          }}
          localization={localization}
          columns={columns}
          data={data.entries}
        />
      </div>
    </section>
  );
}

const styledHeaderSticky = { position: 'sticky', top: 0, zIndex: 10 };
